import { cacheLongTimer, llmGatewayApi, thunkLoader, thunks } from "@app/api";
import { selectLlmGatewayKeysForTable } from "@app/deploy";
import { call, createSelector, parallel, select } from "@app/fx";
import { type WebState, schema } from "@app/schema";
import type {
  DeployLlmGatewayKey,
  LlmGatewayRequest,
  LlmGatewayRequestDetailResponse,
  LlmGatewayRequestResponse,
  LlmGatewayRequestStatus,
  LlmGatewayRequestsResponse,
  LlmGatewayUsage,
  LlmGatewayUsageResponse,
} from "@app/types";
import { DateTime } from "luxon";

export const llmGatewayUsageId = (orgId: string, llmKeyId = "") =>
  `${orgId}:${llmKeyId || "org"}`;

/** The start of the current UTC month through now, as ISO 8601 timestamps. */
export const currentMonthWindow = () => {
  const now = DateTime.utc();
  return {
    startTime: now.startOf("month").toISO() || "",
    endTime: now.toISO() || "",
  };
};

export const deserializeLlmGatewayUsage = (
  payload: LlmGatewayUsageResponse,
): LlmGatewayUsage => {
  const llmKeyId = payload.llm_key_id === null ? "" : `${payload.llm_key_id}`;
  const successfulRequests = Math.round(
    (payload.requests * payload.success_rate) / 100,
  );

  return {
    id: llmGatewayUsageId(payload.organization_id, llmKeyId),
    organizationId: payload.organization_id,
    llmKeyId,
    startTime: payload.start_time,
    endTime: payload.end_time,
    totalRequests: payload.requests,
    successfulRequests,
    failedRequests: payload.requests - successfulRequests,
    averageLatencyMs: payload.average_latency_ms,
    totalTokens: payload.total_tokens,
    inputTokens: payload.prompt_tokens,
    outputTokens: payload.completion_tokens,
    spend: payload.cost_usd,
  };
};

export const selectLlmGatewayUsage = (
  s: WebState,
  { orgId, llmKeyId = "" }: { orgId: string; llmKeyId?: string },
) =>
  schema.llmGatewayUsage.selectById(s, {
    id: llmGatewayUsageId(orgId, llmKeyId),
  });

export interface LlmGatewayKeyRow extends DeployLlmGatewayKey {
  usage: LlmGatewayUsage | null;
}

export const selectLlmGatewayKeysWithUsage = createSelector(
  selectLlmGatewayKeysForTable,
  schema.llmGatewayUsage.selectTable,
  (_: WebState, props: { orgId: string }) => props.orgId,
  (keys, usageById, orgId): LlmGatewayKeyRow[] =>
    keys
      .filter((key) => key.organizationId === orgId)
      .map((key) => ({
        ...key,
        usage: usageById[llmGatewayUsageId(orgId, key.id)] || null,
      })),
);

export const selectActiveLlmGatewayKeyIds = createSelector(
  selectLlmGatewayKeysForTable,
  (_: WebState, props: { orgId: string }) => props.orgId,
  (keys, orgId) =>
    keys
      .filter((key) => key.organizationId === orgId && key.status === "ACTIVE")
      .map((key) => key.id),
);

interface FetchLlmGatewayUsageProps {
  orgId: string;
  llmKeyId?: string;
  startTime: string;
  endTime: string;
}

export const fetchLlmGatewayUsage = llmGatewayApi.get<
  FetchLlmGatewayUsageProps,
  LlmGatewayUsageResponse
>("/api/usage", function* (ctx, next) {
  const { orgId, llmKeyId, startTime, endTime } = ctx.payload;
  const params = new URLSearchParams({
    organization_id: orgId,
    start_time: startTime,
    end_time: endTime,
  });
  if (llmKeyId) {
    params.set("llm_key_id", llmKeyId);
  }
  ctx.request = ctx.req({ url: `${ctx.req().url}?${params}` });

  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  const usage = deserializeLlmGatewayUsage(ctx.json.value);
  yield* schema.update(schema.llmGatewayUsage.add({ [usage.id]: usage }));
});

const KEY_USAGE_BATCH_SIZE = 5;

// One request per key, in small batches, until the gateway can report usage for
// several keys at once.
export const fetchLlmGatewayKeysUsage = thunks.create<{
  orgId: string;
  llmKeyIds: string[];
  startTime: string;
  endTime: string;
}>("llm-gateway-keys-usage", [
  thunkLoader,
  function* (ctx, next) {
    const { orgId, startTime, endTime } = ctx.payload;

    // Usage already in the store is reused until the page is reloaded.
    const usageById = yield* select(schema.llmGatewayUsage.selectTable);
    const llmKeyIds = ctx.payload.llmKeyIds.filter(
      (llmKeyId) => !usageById[llmGatewayUsageId(orgId, llmKeyId)],
    );

    for (let i = 0; i < llmKeyIds.length; i += KEY_USAGE_BATCH_SIZE) {
      const batch = llmKeyIds.slice(i, i + KEY_USAGE_BATCH_SIZE);
      const group = yield* parallel(
        batch.map(
          (llmKeyId) => () =>
            fetchLlmGatewayUsage.run(
              fetchLlmGatewayUsage({ orgId, llmKeyId, startTime, endTime }),
            ),
        ),
      );
      yield* group;
    }

    yield* next();
  },
]);

export const deserializeLlmGatewayRequest = (
  payload: LlmGatewayRequestResponse,
): LlmGatewayRequest => ({
  id: payload.request_id,
  llmKeyId: `${payload.llm_key_id}`,
  timestamp: payload.timestamp,
  operation: payload.operation,
  stream: payload.stream,
  status: payload.status,
  latencyMs: payload.latency_ms,
  inputTokens: payload.prompt_tokens,
  outputTokens: payload.completion_tokens,
  totalTokens: payload.total_tokens,
  cost: payload.cost_usd,
  model: payload.model,
});

export const selectLlmGatewayRequestPage = createSelector(
  schema.llmGatewayRequestPages.selectTable,
  schema.llmGatewayRequests.selectTable,
  (_: WebState, props: { key: string }) => props.key,
  (pages, requestsById, key) => {
    const page = pages[key];
    if (!page) {
      return null;
    }

    return {
      total: page.total,
      requests: page.requestIds
        .map((id) => requestsById[id])
        .filter((request): request is LlmGatewayRequest => !!request),
    };
  },
);

export type LlmGatewayRequestsSortBy =
  | "timestamp"
  | "latency"
  | "tokens"
  | "cost";

export interface FetchLlmGatewayRequestsProps {
  orgId: string;
  llmKeyId: string;
  startTime: string;
  endTime: string;
  status: LlmGatewayRequestStatus | "";
  model: string;
  sortBy: LlmGatewayRequestsSortBy;
  order: "asc" | "desc";
  limit: number;
  offset: number;
}

/* Logs are immutable, so pages stay in the store and revisiting one within the
 * cache window renders from there without a request.
 */
export const fetchLlmGatewayRequests = llmGatewayApi.get<
  FetchLlmGatewayRequestsProps,
  LlmGatewayRequestsResponse
>("/api/logs", { supervisor: cacheLongTimer() }, function* (ctx, next) {
  const { orgId, llmKeyId, startTime, endTime, status, model } = ctx.payload;
  const params = new URLSearchParams({
    organization_id: orgId,
    llm_key_id: llmKeyId,
    start_time: startTime,
    end_time: endTime,
    sort_by: ctx.payload.sortBy,
    order: ctx.payload.order,
    limit: `${ctx.payload.limit}`,
    offset: `${ctx.payload.offset}`,
  });
  if (status) {
    params.set("status", status);
  }
  if (model) {
    params.set("models", model);
  }
  ctx.request = ctx.req({ url: `${ctx.req().url}?${params}` });

  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  const requests = ctx.json.value.requests.map(deserializeLlmGatewayRequest);
  yield* schema.update([
    schema.llmGatewayRequests.add(
      Object.fromEntries(requests.map((request) => [request.id, request])),
    ),
    schema.llmGatewayRequestPages.add({
      [ctx.key]: {
        id: ctx.key,
        requestIds: requests.map((request) => request.id),
        total: ctx.json.value.total,
      },
    }),
  ]);
});

export const selectLlmGatewayRequestById = schema.llmGatewayRequests.selectById;

interface FetchLlmGatewayRequestProps {
  orgId: string;
  llmKeyId: string;
  requestId: string;
}

export const fetchLlmGatewayRequest = llmGatewayApi.get<
  FetchLlmGatewayRequestProps,
  LlmGatewayRequestDetailResponse
>("/api/logs/:llmKeyId/:requestId", function* (ctx, next) {
  const params = new URLSearchParams({ organization_id: ctx.payload.orgId });
  ctx.request = ctx.req({ url: `${ctx.req().url}?${params}` });

  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  const request = deserializeLlmGatewayRequest(ctx.json.value);
  yield* schema.update(
    schema.llmGatewayRequests.add({ [request.id]: request }),
  );
});

const requestRecordId = (requestId: string) =>
  `llm-gateway-request-record:${requestId}`;

export const selectLlmGatewayRequestRecord = (
  s: WebState,
  { requestId }: { requestId: string },
): string =>
  schema.cache.selectById(s, { id: requestRecordId(requestId) }) || "";

// The record's presigned URL expires minutes after it's issued, so every fetch
// asks the gateway for a fresh one first.
export const fetchLlmGatewayRequestRecord =
  thunks.create<FetchLlmGatewayRequestProps>(
    "llm-gateway-request-record",
    { supervisor: cacheLongTimer() },
    [
      thunkLoader,
      function* (ctx, next) {
        const detail = yield* call(() =>
          fetchLlmGatewayRequest.run(fetchLlmGatewayRequest(ctx.payload)),
        );
        if (!detail.json.ok) {
          throw new Error(detail.json.error.message);
        }

        const { url } = detail.json.value;
        if (!url) {
          throw new Error(
            "The record for this request could not be loaded at this time.",
          );
        }

        // A presigned S3 URL, so it's fetched without the gateway's auth header.
        const response = yield* call(() => fetch(url));
        if (!response.ok) {
          throw new Error(
            "The record for this request could not be loaded at this time.",
          );
        }
        const record = yield* call(() => response.text());

        yield* schema.update(
          schema.cache.add({
            [requestRecordId(ctx.payload.requestId)]: record,
          }),
        );
        yield* next();
      },
    ],
  );
